import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Alert,
  Box, Button, TextField, Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';

import ApiClient from '../../loopback/apiClient';
import GenericDialog from '../GenericDialog';
import ContentBox from '../layout/ContentBox';
import { QuipUser } from '../../redux/types';
import { ExtendedEnrollment } from '../../views/EvaluationDialog';

type Props = {
  isOpen: boolean,
  onClose: () => void,
  enrollment: ExtendedEnrollment,
  extendedSubmissionDate?: string
  trialCode: string,
  user: QuipUser,
  trialEndDate: string,
}

const PostResubmitDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  enrollment,
  extendedSubmissionDate,
  trialCode,
  trialEndDate,
  user,
}: Props) => {
  const trialHasEnded = moment.utc(trialEndDate).add(1, 'd').diff(moment.utc()) < 0;
  const minDate = trialHasEnded ? moment.utc() : moment.utc(trialEndDate);
  const [extendedDate, setExtendedDate] = useState<Moment | null>(
    extendedSubmissionDate ? moment(extendedSubmissionDate) : minDate,
  );
  return (
    <GenericDialog
      maxWidth="sm"
      isOpen={isOpen}
      onClose={onClose}
      closeButtonLabel={<FormattedMessage id="genericClose" />}
      actions={[
        <Button
          key="confirmBtn"
          color="primary"
          variant="contained"
          onClick={() => {
            if (extendedDate) {
              ApiClient.allowPostResubmit(enrollment.id, extendedDate.format()).then(onClose);
            }
          }}
        >
          <FormattedMessage id="confirm" />
        </Button>,
      ]}
    >
      <ContentBox title="Allow post resubmit" elevation={0}>
        <Typography>
          <FormattedMessage
            id="trialdetails.postResubmitMessage"
            values={{ trialCode, title: user.title, lastName: user.surname }}
          />
        </Typography>
        <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              // eslint-disable-next-line react/jsx-props-no-spreading
              renderInput={(props) => <TextField fullWidth {...props} label={<FormattedMessage id="trials.endDateUntil" />} />}
              value={extendedDate}
              views={['year', 'month', 'day']}
              onChange={(date?: Moment | null) => setExtendedDate(date || null)}
              inputFormat="DD.MM.yyyy"
              mask="__.__.____"
              orientation="landscape"
              openTo="day"
              minDate={minDate}
            />
          </LocalizationProvider>
          {(enrollment.submission?.finalised || enrollment.surveySubmissionMeta?.finalized) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <FormattedMessage id="trialdetails.postResubmitWarning" />
            </Alert>
          )}
          {!trialHasEnded && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <FormattedMessage id="trialdetails.postResubmitTrialNotEnd" />
            </Alert>
          )}
        </Box>
      </ContentBox>
    </GenericDialog>
  );
};

export default PostResubmitDialog;
